import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { toUsFormat } from 'utils/utils';

import styles from './PunchupSendTicketsConfirmation.module.css';

export interface PunchupSendTicketsConfirmationProps {
	children?: ReactNode;
	/** Money total displayed at the bottom of the body, before the button row. */
	totalPrice?: number;
	/** Overrides the send-button label. Defaults to the i18n `button-send` ("Send"). */
	sendLabel?: string;
	isSending?: boolean;
	/** When provided the send button fires this callback (type="button"). When omitted the button
	 * uses type="submit" and delegates to the enclosing form's onSubmit. */
	onSend?: () => void;
	onCancel: () => void;
}

export const PunchupSendTicketsConfirmation = ({
	children,
	totalPrice,
	sendLabel,
	isSending = false,
	onSend,
	onCancel,
}: PunchupSendTicketsConfirmationProps) => {
	const { t } = useTranslation();

	return (
		<div className={styles.root}>
			<div className={styles.confirmationCard}>
				<h3 className={styles.confirmationHeading}>{t('send-tickets-confirmation-heading', 'Confirmation')}</h3>
				{children && <div className={styles.body}>{children}</div>}
				{totalPrice !== undefined && (
					<p className={styles.total}>
						<span className={styles.totalLabel}>{t('send-tickets-total', 'Total')}</span>
						<strong className={totalPrice > 0 ? styles.totalSum : undefined}>{toUsFormat(totalPrice)}</strong>
					</p>
				)}
				<div className={styles.buttonRow}>
					<PunchupButton text={t('button-cancel', 'Cancel')} type="button" variant="secondary" onClick={onCancel} />
					<PunchupButton
						text={sendLabel ?? t('button-send', 'Send')}
						type={onSend ? 'button' : 'submit'}
						isLoading={isSending}
						onClick={onSend}
					/>
				</div>
			</div>
		</div>
	);
};
